import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactPlayer from 'react-player';
import FreeThrowMetricsPanel from './FreeThrowMetricsPanel';
import FreeThrowSpeedProfile from './FreeThrowSpeedProfile';
import FreeThrowVideoOverlay from './FreeThrowVideoOverlay';
import FreeThrowChatPanel from './FreeThrowChatPanel';

/**
 * FreeThrowTab — Main dashboard tab for FT biomechanical analysis
 *
 * Layout:
 *   Hero Cards (3 coach metrics) with tooltips
 *   Inter-Phase Speed Profile
 *   Video Player + Overlay | Metrics Panel
 *   Triptych Multi-Angle Analysis (if available)
 *   Shot-by-Shot Table (sorted by speed pre-shot, then outcome)
 *   Biomechanical Tells (Phase 2)
 *   Trend Charts
 *
 * Data loaded from GCS prefix analysis/vw-fts/v1 via /api/gcs-proxy/
 */

const API_BASE = process.env.REACT_APP_API_URL || '';
const GCS_PREFIX = 'analysis/vw-fts/v1';
const FT_LOCAL_PREFIX = 'free_throw_v1';

// ─── Styles ──────────────────────────────────────────────────────────────────

const cardStyle = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 12,
  padding: 20,
};

const statBoxStyle = (color) => ({
  ...cardStyle,
  borderLeft: `3px solid ${color}`,
  textAlign: 'center',
  minWidth: 140,
  position: 'relative',
});

const badgeStyle = (color) => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 6,
  fontSize: 11,
  fontWeight: 600,
  background: `${color}22`,
  color: color,
  border: `1px solid ${color}44`,
});

const OUTCOME_COLORS = {
  made: '#22c55e',
  missed_short: '#ef4444',
  missed_long: '#f97316',
  missed_left: '#8b5cf6',
  missed_right: '#06b6d4',
  missed_rim_in_out: '#eab308',
  missed_airball: '#ec4899',
  not_visible: '#6b7280',
};

const TELL_SEVERITY_COLORS = {
  CRITICAL: '#ef4444',
  HIGH: '#f97316',
  MEDIUM: '#eab308',
  LOW: '#6b7280',
};

// ─── Tooltip Component ──────────────────────────────────────────────────────

const tooltipContainerStyle = {
  position: 'relative',
  display: 'inline-block',
};

const tooltipTriggerStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 14,
  height: 14,
  borderRadius: '50%',
  fontSize: 9,
  fontWeight: 700,
  color: '#666',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  cursor: 'help',
  marginLeft: 4,
  verticalAlign: 'middle',
  lineHeight: 1,
};

const tooltipBodyStyle = {
  position: 'absolute',
  bottom: '100%',
  left: '50%',
  transform: 'translateX(-50%)',
  marginBottom: 8,
  padding: '10px 14px',
  background: 'rgba(15,15,20,0.95)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 8,
  fontSize: 11,
  lineHeight: 1.5,
  color: '#ccc',
  minWidth: 220,
  maxWidth: 320,
  zIndex: 50,
  boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
  whiteSpace: 'pre-line',
  pointerEvents: 'none',
};

function Tooltip({ text, children, position = 'top' }) {
  const [show, setShow] = useState(false);
  const posStyle = position === 'bottom'
    ? { top: '100%', bottom: 'auto', marginTop: 8, marginBottom: 0 }
    : {};

  return (
    <span
      style={tooltipContainerStyle}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children || <span style={tooltipTriggerStyle}>?</span>}
      {show && (
        <div style={{ ...tooltipBodyStyle, ...posStyle }}>
          {text}
        </div>
      )}
    </span>
  );
}

// ─── Tooltip Text Definitions ───────────────────────────────────────────────

const TOOLTIPS = {
  shotSpeed: 'Total time from ball raise to release, measured in tenths of a second. Extracted by AI from video frame analysis. Lower = faster release. NBA average ~5-7 tenths. This is Coach Metric #1.',
  kneeAngle: 'Minimum angle at the knee joint during the dip phase (hip→knee→ankle). Extracted from MediaPipe landmarks 23-28. Deeper dip (lower angle) = more leg power. This is Coach Metric #2.',
  toeDuration: 'Time on toes after release until heels touch ground, in tenths. Longer toe duration = better follow-through commitment and leg drive. This is Coach Metric #3.',
  ftHold: 'How long the shooter holds follow-through after release, in tenths. Longer hold = more consistent finish. "Rock solid" stability is the target.',
  ftPct: 'Free throw percentage calculated from analyzed attempts. Not season stats — this is from the video analysis sample.',
  speedProfile: 'Shows timing between each biomechanical marker transition. Green = faster than made-shot baseline. Red = slower. The AI extracts timestamps at each phase transition and computes inter-phase durations.',
  dipToRaise: 'Transition time from lowest knee dip to start of ball raise (ms). This "loading" phase transfers leg energy to the shot. Rushed transitions (<200ms) correlate with missed_short shots.',
  raiseToSet: 'Time from ball raise initiation to set point (highest pre-release position). This is the arm acceleration phase. Consistency here is more important than raw speed.',
  setToRelease: 'Time from set point to ball release — the final "push." Very short phase (100-150ms). Variations here indicate grip or timing issues.',
  kneeAngleCol: 'Minimum knee angle during the dip. Lower = deeper bend = more leg power. Measured from hip→knee→ankle landmarks. Angles >150° often correlate with missed_short.',
  dipTiming: 'When the knee dip occurs relative to ball raise: "before" (ideal), "during" (acceptable), "simultaneous" (risky), or "after" (problematic). "Before ball raise" gives best energy transfer.',
  toeCol: 'Toe duration in tenths — time on toes post-release. Measures leg drive commitment. Short duration (<3.5t) = early heel drop, often correlating with missed_short shots.',
  holdCol: 'Follow-through hold duration in tenths. How long the arm stays extended after release. Shorter holds correlate with inconsistency.',
  ballStart: 'Where the ball is when upward motion begins. "Chest/centered" is the standard pocket position. "Waist" or "hip" starting positions change the shot arc.',
  missType: 'NOT make/miss — specific miss classification. The AI identifies the miss direction and type from video, enabling pattern analysis (e.g., "80% of missed_short have shallow dip").',
  tellSection: 'Biomechanical tells are patterns that predict specific miss types. Identified by Phase 2 comparative synthesis — the AI compares blind Phase 1 observations against known outcomes to find correlations that pass statistical thresholds (>60% in target, <30% in made).',
  missBreakdown: 'Distribution of shot outcomes from the analyzed sample. Uses specific miss types (short, long, left, right, rim_in_out, airball) instead of binary make/miss to enable targeted coaching.',
  speedByOutcome: 'Average total shot speed grouped by outcome. Reveals whether speed correlates with miss direction — e.g., faster shots may go long, slower shots may fall short.',
  triptych: 'Multi-angle analysis from a triptych-style broadcast video (3 camera angles side-by-side). Each angle provides different biomechanical signal quality. The AI analyzes all 3 angles independently (Phase 1 blind), then fuses the best metrics from each angle.',
  confidence: 'AI confidence in the analysis. Higher = clearer video, more visible landmarks. Multi-angle fusion typically achieves higher confidence than single-angle analysis.',
  sortOrder: 'Table is sorted by: (1) total shot speed fastest-to-slowest, then (2) outcome type. This lets coaches see if speed variations correlate with outcomes.',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function avg(arr) {
  if (!arr || arr.length === 0) return null;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function sortAttempts(attempts) {
  if (!attempts) return [];
  return [...attempts].sort((a, b) => {
    const speedA = a.inter_phase_speed?.total_shot_speed_tenths || 999;
    const speedB = b.inter_phase_speed?.total_shot_speed_tenths || 999;
    if (speedA !== speedB) return speedA - speedB;
    const outcomeOrder = ['made', 'missed_short', 'missed_long', 'missed_left', 'missed_right', 'missed_rim_in_out', 'missed_airball', 'not_visible'];
    return outcomeOrder.indexOf(a.shot_outcome || 'not_visible') - outcomeOrder.indexOf(b.shot_outcome || 'not_visible');
  });
}

// ─── Hero Cards ──────────────────────────────────────────────────────────────

function HeroCards({ attempts, playerSummary }) {
  const stats = useMemo(() => {
    if (playerSummary) {
      return {
        avgSpeed: playerSummary.avg_shot_speed_tenths,
        avgKnee: playerSummary.avg_knee_angle,
        avgToe: playerSummary.avg_toe_duration_tenths,
        avgHold: playerSummary.avg_follow_through_hold_tenths,
        totalAttempts: playerSummary.total_attempts || 0,
        madeCount: playerSummary.made_count || 0,
      };
    }
    if (!attempts || attempts.length === 0) return null;
    return {
      avgSpeed: avg(attempts.filter(a => a.inter_phase_speed?.total_shot_speed_tenths > 0).map(a => a.inter_phase_speed.total_shot_speed_tenths)),
      avgKnee: avg(attempts.filter(a => a.knee_angle?.min_knee_angle_degrees != null).map(a => a.knee_angle.min_knee_angle_degrees)),
      avgToe: avg(attempts.filter(a => a.heel_contact?.toe_duration_tenths > 0).map(a => a.heel_contact.toe_duration_tenths)),
      avgHold: avg(attempts.filter(a => a.follow_through?.hold_duration_tenths > 0).map(a => a.follow_through.hold_duration_tenths)),
      totalAttempts: attempts.length,
      madeCount: attempts.filter(a => a.shot_outcome === 'made').length,
    };
  }, [attempts, playerSummary]);

  if (!stats) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center', color: '#888', padding: 40 }}>
        No free throw data loaded yet
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
      <div style={statBoxStyle('#6366f1')}>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#fff' }}>
          {stats.avgSpeed != null ? stats.avgSpeed.toFixed(1) : '—'}
          <span style={{ fontSize: 12, fontWeight: 400, color: '#888' }}>t</span>
        </div>
        <div style={{ fontSize: 11, color: '#888' }}>Avg Shot Speed <Tooltip text={TOOLTIPS.shotSpeed} /></div>
        <div style={{ fontSize: 10, color: '#6366f1' }}>raise → release</div>
      </div>
      <div style={statBoxStyle('#f59e0b')}>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#fff' }}>
          {stats.avgKnee != null ? Math.round(stats.avgKnee) : '—'}
          <span style={{ fontSize: 12, fontWeight: 400, color: '#888' }}>°</span>
        </div>
        <div style={{ fontSize: 11, color: '#888' }}>Avg Knee Angle <Tooltip text={TOOLTIPS.kneeAngle} /></div>
        <div style={{ fontSize: 10, color: '#f59e0b' }}>lowest dip point</div>
      </div>
      <div style={statBoxStyle('#22c55e')}>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#fff' }}>
          {stats.avgToe != null ? stats.avgToe.toFixed(1) : '—'}
          <span style={{ fontSize: 12, fontWeight: 400, color: '#888' }}>t</span>
        </div>
        <div style={{ fontSize: 11, color: '#888' }}>Avg Toe Duration <Tooltip text={TOOLTIPS.toeDuration} /></div>
        <div style={{ fontSize: 10, color: '#22c55e' }}>release → heel land</div>
      </div>
      <div style={statBoxStyle('#06b6d4')}>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#fff' }}>
          {stats.avgHold != null ? stats.avgHold.toFixed(1) : '—'}
          <span style={{ fontSize: 12, fontWeight: 400, color: '#888' }}>t</span>
        </div>
        <div style={{ fontSize: 11, color: '#888' }}>Avg FT Hold <Tooltip text={TOOLTIPS.ftHold} /></div>
        <div style={{ fontSize: 10, color: '#06b6d4' }}>follow-through duration</div>
      </div>
      <div style={statBoxStyle('#a855f7')}>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#fff' }}>
          {stats.totalAttempts > 0 ? `${((stats.madeCount / stats.totalAttempts) * 100).toFixed(1)}%` : '—'}
        </div>
        <div style={{ fontSize: 11, color: '#888' }}>FT% <Tooltip text={TOOLTIPS.ftPct} /></div>
        <div style={{ fontSize: 10, color: '#a855f7' }}>{stats.madeCount}/{stats.totalAttempts} attempts</div>
      </div>
    </div>
  );
}

// ─── Shot Table ──────────────────────────────────────────────────────────────

function ShotTable({ attempts, selectedId, onSelect }) {
  const sorted = useMemo(() => sortAttempts(attempts), [attempts]);

  if (!sorted.length) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center', color: '#888' }}>
        No attempts to display
      </div>
    );
  }

  const headerCell = { padding: '8px 6px', fontSize: 11, fontWeight: 700, color: '#888', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)' };
  const dataCell = (isSelected) => ({ padding: '6px', fontSize: 12, textAlign: 'center', color: '#ccc', background: isSelected ? 'rgba(99,102,241,0.15)' : 'transparent', cursor: 'pointer' });

  return (
    <div style={{ ...cardStyle, padding: 0, overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={headerCell}>#</th>
            <th style={headerCell}>Shooter</th>
            <th style={headerCell}>Speed <Tooltip text={TOOLTIPS.shotSpeed} /></th>
            <th style={headerCell}>Dip→Raise <Tooltip text={TOOLTIPS.dipToRaise} /></th>
            <th style={headerCell}>Raise→Set <Tooltip text={TOOLTIPS.raiseToSet} /></th>
            <th style={headerCell}>Set→Rel <Tooltip text={TOOLTIPS.setToRelease} /></th>
            <th style={headerCell}>Knee° <Tooltip text={TOOLTIPS.kneeAngleCol} /></th>
            <th style={headerCell}>Dip Time <Tooltip text={TOOLTIPS.dipTiming} /></th>
            <th style={headerCell}>Toe <Tooltip text={TOOLTIPS.toeCol} /></th>
            <th style={headerCell}>Hold <Tooltip text={TOOLTIPS.holdCol} /></th>
            <th style={headerCell}>Ball Start <Tooltip text={TOOLTIPS.ballStart} /></th>
            <th style={headerCell}>Result <Tooltip text={TOOLTIPS.missType} /></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((a, i) => {
            const sp = a.inter_phase_speed || {};
            const kn = a.knee_angle || {};
            const hl = a.heel_contact || {};
            const ft = a.follow_through || {};
            const bl = a.ball_start || {};
            const isSelected = (a.clip_id || i) === selectedId;
            const oc = a.shot_outcome || 'not_visible';
            const ocColor = OUTCOME_COLORS[oc];

            return (
              <tr key={a.clip_id || i} onClick={() => onSelect(a.clip_id || i)} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={dataCell(isSelected)}>{i + 1}</td>
                <td style={{ ...dataCell(isSelected), textAlign: 'left', fontWeight: 600, color: '#fff' }}>{a.shooter || '—'}</td>
                <td style={dataCell(isSelected)}>
                  <span style={{ color: '#6366f1', fontWeight: 700 }}>
                    {sp.total_shot_speed_tenths > 0 ? sp.total_shot_speed_tenths.toFixed(1) : '—'}
                  </span>
                </td>
                <td style={dataCell(isSelected)}>{sp.dip_to_raise_ms > 0 ? (sp.dip_to_raise_ms / 100).toFixed(1) : '—'}</td>
                <td style={dataCell(isSelected)}>{sp.raise_to_set_point_ms > 0 ? (sp.raise_to_set_point_ms / 100).toFixed(1) : '—'}</td>
                <td style={dataCell(isSelected)}>{sp.set_point_to_release_ms > 0 ? (sp.set_point_to_release_ms / 100).toFixed(1) : '—'}</td>
                <td style={dataCell(isSelected)}>
                  <span style={{ color: '#f59e0b' }}>
                    {kn.min_knee_angle_degrees != null ? Math.round(kn.min_knee_angle_degrees) + '°' : '—'}
                  </span>
                </td>
                <td style={dataCell(isSelected)}>
                  {kn.dip_timing && kn.dip_timing !== 'not_detected' ? kn.dip_timing.replace(/_/g, ' ') : '—'}
                </td>
                <td style={dataCell(isSelected)}>
                  <span style={{ color: '#22c55e' }}>
                    {hl.toe_duration_tenths > 0 ? hl.toe_duration_tenths.toFixed(1) : '—'}
                  </span>
                </td>
                <td style={dataCell(isSelected)}>
                  {ft.hold_duration_tenths > 0 ? ft.hold_duration_tenths.toFixed(1) : '—'}
                </td>
                <td style={dataCell(isSelected)}>
                  {bl.height_relative_to_body || '—'}
                </td>
                <td style={dataCell(isSelected)}>
                  <span style={badgeStyle(ocColor)}>
                    {oc.replace(/^missed_/, '').replace(/_/g, ' ')}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Biomechanical Tells ─────────────────────────────────────────────────────

function BiomechanicalTells({ synthesis }) {
  if (!synthesis?.identified_tells || synthesis.identified_tells.length === 0) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center', color: '#888' }}>
        Biomechanical tells will appear after Phase 2 comparative synthesis completes.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: 0 }}>
        Biomechanical Tells — THE FT WIRE
        <Tooltip text={TOOLTIPS.tellSection} />
      </h3>
      {synthesis.identified_tells.map((tell, i) => {
        const sevColor = TELL_SEVERITY_COLORS[tell.severity] || '#6b7280';
        return (
          <div key={i} style={{ ...cardStyle, borderLeft: `3px solid ${sevColor}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={badgeStyle(sevColor)}>{tell.severity}</span>
              <span style={{ fontSize: 11, color: '#888' }}>
                {tell.frequency_in_target ? `${(tell.frequency_in_target * 100).toFixed(0)}% in ${tell.target_outcome || 'target'}` : ''}
                {tell.frequency_in_made != null ? ` vs ${(tell.frequency_in_made * 100).toFixed(0)}% in made` : ''}
              </span>
            </div>
            <div style={{ fontSize: 13, color: '#fff', fontWeight: 600, marginBottom: 4 }}>
              {tell.description}
            </div>
            {tell.biomechanical_marker && (
              <div style={{ fontSize: 11, color: '#a5b4fc' }}>
                Marker: {tell.biomechanical_marker}
              </div>
            )}
            {tell.evidence_summary && (
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                {tell.evidence_summary}
              </div>
            )}
          </div>
        );
      })}

      {/* Coaching Recommendations */}
      {synthesis.coaching_recommendations && synthesis.coaching_recommendations.length > 0 && (
        <>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: '12px 0 0' }}>
            Coaching Recommendations
          </h4>
          {synthesis.coaching_recommendations.map((rec, i) => (
            <div key={i} style={{ ...cardStyle, borderLeft: '3px solid #06b6d4' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
                  P{rec.priority || i + 1}: {rec.correction}
                </span>
                {rec.expected_impact && (
                  <span style={{ fontSize: 11, color: '#22c55e' }}>
                    {rec.expected_impact}
                  </span>
                )}
              </div>
              {rec.drill && (
                <div style={{ fontSize: 12, color: '#67e8f9' }}>
                  Drill: {rec.drill}
                </div>
              )}
              {rec.related_tell && (
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  Related: {rec.related_tell}
                </div>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ─── Outcome Breakdown Chart ─────────────────────────────────────────────────

function OutcomeBreakdown({ attempts }) {
  const counts = useMemo(() => {
    const c = {};
    (attempts || []).forEach(a => {
      const oc = a.shot_outcome || 'not_visible';
      c[oc] = (c[oc] || 0) + 1;
    });
    return c;
  }, [attempts]);

  const total = attempts?.length || 0;
  if (total === 0) return null;

  const outcomes = Object.entries(counts).sort((a, b) => b[1] - a[1]);

  return (
    <div style={cardStyle}>
      <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: '#fff' }}>
        Miss Type Breakdown <Tooltip text={TOOLTIPS.missBreakdown} />
      </h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {outcomes.map(([oc, count]) => {
          const color = OUTCOME_COLORS[oc] || '#6b7280';
          const pct = (count / total) * 100;
          return (
            <div key={oc} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 90, fontSize: 11, color: '#888', textAlign: 'right' }}>
                {oc.replace(/^missed_/, '').replace(/_/g, ' ')}
              </div>
              <div style={{ flex: 1, height: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: `${color}66`,
                  borderRight: `2px solid ${color}`,
                  borderRadius: 4,
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <div style={{ width: 60, fontSize: 11, color: '#ccc', textAlign: 'right' }}>
                {count} ({pct.toFixed(0)}%)
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Speed by Outcome Chart ──────────────────────────────────────────────────

function SpeedByOutcome({ attempts }) {
  const groups = useMemo(() => {
    const g = {};
    (attempts || []).forEach(a => {
      const oc = a.shot_outcome || 'not_visible';
      if (!g[oc]) g[oc] = [];
      const speed = a.inter_phase_speed?.total_shot_speed_tenths;
      if (speed > 0) g[oc].push(speed);
    });
    return Object.entries(g)
      .map(([oc, speeds]) => ({ outcome: oc, avg: avg(speeds), count: speeds.length }))
      .filter(g => g.avg != null)
      .sort((a, b) => a.avg - b.avg);
  }, [attempts]);

  if (groups.length === 0) return null;

  const maxAvg = Math.max(...groups.map(g => g.avg), 1);

  return (
    <div style={cardStyle}>
      <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: '#fff' }}>
        Avg Speed by Outcome <Tooltip text={TOOLTIPS.speedByOutcome} />
      </h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {groups.map(g => {
          const color = OUTCOME_COLORS[g.outcome] || '#6b7280';
          const pct = (g.avg / maxAvg) * 100;
          return (
            <div key={g.outcome} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 90, fontSize: 11, color: '#888', textAlign: 'right' }}>
                {g.outcome.replace(/^missed_/, '').replace(/_/g, ' ')}
              </div>
              <div style={{ flex: 1, height: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: `${color}44`,
                  borderRight: `2px solid ${color}`,
                  borderRadius: 4,
                }} />
              </div>
              <div style={{ width: 60, fontSize: 11, color: '#ccc', textAlign: 'right' }}>
                {g.avg.toFixed(1)}t
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Triptych Multi-Angle Analysis Section ──────────────────────────────────

const ANGLE_COLORS = {
  sideline: '#f59e0b',
  overhead: '#6366f1',
  behind_basket: '#22c55e',
};

const ANGLE_BEST_FOR = {
  sideline: 'Knee angle, inter-phase speed, heel contact timing',
  overhead: 'Ball start position, shot trajectory, outcome detection',
  behind_basket: 'Follow-through direction, wrist angle, finger alignment',
};

function TriptychSection({ triptych }) {
  if (!triptych) return null;

  const attempts = triptych.fused_attempts || [];
  if (attempts.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: 0 }}>
          Triptych Multi-Angle Analysis
          <Tooltip text={TOOLTIPS.triptych} />
        </h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {Object.entries(ANGLE_COLORS).map(([angle, color]) => (
            <span key={angle} style={{ fontSize: 10, color }}>
              ● {angle.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      </div>

      {/* Source info */}
      <div style={{ ...cardStyle, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontSize: 12, color: '#fff', fontWeight: 600 }}>{triptych.shooter}</span>
          <span style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>{triptych.source_video}</span>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <span style={{ fontSize: 11, color: '#888' }}>
            {triptych.total_attempts} attempts fused from 3 angles
          </span>
          <span style={badgeStyle('#a855f7')}>
            {triptych.model || 'Gemini 3.1 Pro'}
          </span>
        </div>
      </div>

      {/* Angle Signal Quality */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {Object.entries(ANGLE_COLORS).map(([angle, color]) => (
          <div key={angle} style={{ ...cardStyle, borderLeft: `3px solid ${color}`, padding: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 4, textTransform: 'capitalize' }}>
              {angle.replace(/_/g, ' ')}
            </div>
            <div style={{ fontSize: 10, color: '#888' }}>Best signal for:</div>
            <div style={{ fontSize: 11, color: '#ccc', marginTop: 2 }}>
              {ANGLE_BEST_FOR[angle]}
            </div>
          </div>
        ))}
      </div>

      {/* Per-attempt fusion details */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        {attempts.map((a, i) => {
          const oc = a.shot_outcome || 'not_visible';
          const ocColor = OUTCOME_COLORS[oc];
          const conf = a.overall_confidence || 0;

          return (
            <div key={a.clip_id || i} style={{ ...cardStyle, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
                  FT #{a.attempt_number || i + 1}
                </span>
                <span style={badgeStyle(ocColor)}>
                  {oc.replace(/^missed_/, '').replace(/_/g, ' ')}
                </span>
              </div>

              {/* Key metrics from fusion */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 11 }}>
                {a.knee_angle?.min_knee_angle_degrees != null && (
                  <div>
                    <span style={{ color: '#888' }}>Knee: </span>
                    <span style={{ color: '#f59e0b', fontWeight: 600 }}>{Math.round(a.knee_angle.min_knee_angle_degrees)}°</span>
                  </div>
                )}
                {a.follow_through?.hold_duration_tenths > 0 && (
                  <div>
                    <span style={{ color: '#888' }}>Hold: </span>
                    <span style={{ color: '#06b6d4', fontWeight: 600 }}>{a.follow_through.hold_duration_tenths.toFixed(1)}t</span>
                  </div>
                )}
                {a.follow_through?.wrist_angle_at_hold != null && (
                  <div>
                    <span style={{ color: '#888' }}>Wrist: </span>
                    <span style={{ color: '#ccc' }}>{Math.round(a.follow_through.wrist_angle_at_hold)}°</span>
                  </div>
                )}
                {a.follow_through?.finger_direction && a.follow_through.finger_direction !== 'not_visible' && (
                  <div>
                    <span style={{ color: '#888' }}>Fingers: </span>
                    <span style={{ color: '#ccc' }}>{a.follow_through.finger_direction.replace(/_/g, ' ')}</span>
                  </div>
                )}
                {a.ball_start?.height_relative_to_body && (
                  <div>
                    <span style={{ color: '#888' }}>Ball: </span>
                    <span style={{ color: '#ccc' }}>{a.ball_start.height_relative_to_body}</span>
                  </div>
                )}
                {a.heel_contact?.heel_lift_at_release != null && (
                  <div>
                    <span style={{ color: '#888' }}>Heel lift: </span>
                    <span style={{ color: a.heel_contact.heel_lift_at_release ? '#22c55e' : '#ef4444' }}>
                      {a.heel_contact.heel_lift_at_release ? 'yes' : 'no'}
                    </span>
                  </div>
                )}
              </div>

              {/* Confidence bar */}
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#666', marginBottom: 2 }}>
                  <span>Fusion confidence</span>
                  <span>{(conf * 100).toFixed(0)}%</span>
                </div>
                <div style={{ height: 4, background: 'rgba(255,255,255,0.05)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{
                    width: `${conf * 100}%`,
                    height: '100%',
                    background: conf > 0.7 ? '#22c55e' : conf > 0.5 ? '#eab308' : '#ef4444',
                    borderRadius: 2,
                  }} />
                </div>
              </div>

              {a.shot_outcome_detail && (
                <div style={{ fontSize: 10, color: '#888', marginTop: 6, fontStyle: 'italic' }}>
                  {a.shot_outcome_detail}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function FreeThrowTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [synthesis, setSynthesis] = useState(null);
  const [playerSummary, setPlayerSummary] = useState(null);
  const [triptych, setTriptych] = useState(null);
  const [selectedAttemptId, setSelectedAttemptId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [showOverlay, setShowOverlay] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState('all');
  const [subTab, setSubTab] = useState('analysis'); // 'analysis' | 'chat'

  const playerRef = useRef(null);
  const synthesisRef = useRef(null);
  const pollRef = useRef(null);

  // ─── Data Loading ────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (synthesisRef.current) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }

    try {
      const [synthResult, summaryResult, attemptsResult] = await Promise.allSettled([
        fetch(`${API_BASE}/api/gcs-proxy/${GCS_PREFIX}/synthesis.json`).then(r => r.ok ? r.json() : null),
        fetch(`${API_BASE}/api/gcs-proxy/${GCS_PREFIX}/player_summary.json`).then(r => r.ok ? r.json() : null),
        fetch(`${API_BASE}/api/gcs-proxy/${GCS_PREFIX}/attempts.json`).then(r => r.ok ? r.json() : null),
      ]);

      if (synthResult.status === 'fulfilled' && synthResult.value) {
        setSynthesis(synthResult.value);
        synthesisRef.current = synthResult.value;
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }

      if (summaryResult.status === 'fulfilled' && summaryResult.value) {
        setPlayerSummary(summaryResult.value);
      }

      if (attemptsResult.status === 'fulfilled' && attemptsResult.value) {
        const data = Array.isArray(attemptsResult.value) ? attemptsResult.value : [];
        setAttempts(data);
      }

      // Fallback: try local backend endpoint if GCS didn't return data
      const hasGcsAttempts = attemptsResult.status === 'fulfilled' && Array.isArray(attemptsResult.value) && attemptsResult.value.length > 0;
      const hasGcsSynthesis = synthResult.status === 'fulfilled' && synthResult.value && synthResult.value.identified_tells;

      if (!hasGcsAttempts || !hasGcsSynthesis) {
        const localResult = await fetch(`${API_BASE}/api/free-throw/results`).then(r => r.ok ? r.json() : null).catch(() => null);
        if (localResult?.results && !hasGcsAttempts) {
          setAttempts(localResult.results);
        }
        if (localResult?.playerSummary) {
          setPlayerSummary(localResult.playerSummary);
        }
        const localSynth = await fetch(`${API_BASE}/api/free-throw/synthesis`).then(r => r.ok ? r.json() : null).catch(() => null);
        if (localSynth?.synthesis && !hasGcsSynthesis) {
          setSynthesis(localSynth.synthesis);
          synthesisRef.current = localSynth.synthesis;
        }
      }

      // Load triptych data
      const triptychResult = await fetch(`${API_BASE}/api/free-throw/triptych`).then(r => r.ok ? r.json() : null).catch(() => null);
      if (triptychResult?.triptych) {
        setTriptych(triptychResult.triptych);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    pollRef.current = setInterval(fetchData, 30000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchData]);

  // ─── Derived State ───────────────────────────────────────────────────

  const players = useMemo(() => {
    const set = new Set(attempts.map(a => a.shooter).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [attempts]);

  const filteredAttempts = useMemo(() => {
    if (selectedPlayer === 'all') return attempts;
    return attempts.filter(a => a.shooter === selectedPlayer);
  }, [attempts, selectedPlayer]);

  const selectedAttempt = useMemo(() => {
    if (!selectedAttemptId) return filteredAttempts[0] || null;
    return filteredAttempts.find(a => (a.clip_id || filteredAttempts.indexOf(a)) === selectedAttemptId) || null;
  }, [filteredAttempts, selectedAttemptId]);

  const activeSynthesis = useMemo(() => {
    if (!synthesis) return null;
    if (selectedPlayer === 'all') return synthesis;
    return synthesis[selectedPlayer] || synthesis;
  }, [synthesis, selectedPlayer]);

  const activePlayerSummary = useMemo(() => {
    if (!playerSummary) return null;
    if (selectedPlayer === 'all') {
      // If keyed by player, return null so HeroCards computes from attempts
      const keys = Object.keys(playerSummary);
      if (keys.length > 0 && playerSummary[keys[0]]?.shooter) return null;
      return playerSummary; // Legacy flat format
    }
    return playerSummary[selectedPlayer] || playerSummary;
  }, [playerSummary, selectedPlayer]);

  // Show triptych for selected player or all
  const activeTriptych = useMemo(() => {
    if (!triptych) return null;
    if (selectedPlayer === 'all') return triptych;
    if (triptych.shooter === selectedPlayer) return triptych;
    return null;
  }, [triptych, selectedPlayer]);

  // Compute baseline for speed profile (average across made shots)
  const madeBaseline = useMemo(() => {
    const made = filteredAttempts.filter(a => a.shot_outcome === 'made');
    if (made.length === 0) return null;
    const keys = ['stance_to_dip_ms', 'dip_to_raise_ms', 'raise_to_set_point_ms', 'set_point_to_release_ms', 'release_to_follow_through_ms', 'release_to_heel_land_ms'];
    const baseline = {};
    keys.forEach(k => {
      const vals = made.map(a => a.inter_phase_speed?.[k]).filter(v => v > 0);
      baseline[k] = vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    });
    return baseline;
  }, [filteredAttempts]);

  // ─── Render ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center', color: '#888', padding: 60 }}>
        <div style={{ fontSize: 16, marginBottom: 8 }}>Loading Free Throw Analysis...</div>
        <div style={{ fontSize: 12 }}>Checking GCS prefix: {GCS_PREFIX}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#fff' }}>
            Free Throw Biomechanics
          </h2>
          <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
            VW-FTS Study · {filteredAttempts.length} attempts · Phase {synthesis ? '2 Complete' : '1'}
            {triptych && ' · Triptych Available'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Sub-tab toggle */}
          {['analysis', 'chat'].map(tab => (
            <button
              key={tab}
              onClick={() => setSubTab(tab)}
              style={{
                padding: '6px 14px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.15)',
                background: subTab === tab ? (tab === 'chat' ? 'rgba(34,197,94,0.2)' : 'rgba(99,102,241,0.2)') : 'transparent',
                color: subTab === tab ? (tab === 'chat' ? '#86efac' : '#a5b4fc') : '#888',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {tab === 'analysis' ? 'Analysis' : 'Ask AI'}
            </button>
          ))}
          {/* Player selector */}
          <select
            value={selectedPlayer}
            onChange={e => { setSelectedPlayer(e.target.value); setSelectedAttemptId(null); }}
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 8,
              padding: '6px 12px',
              color: '#fff',
              fontSize: 13,
            }}
          >
            {players.map(p => (
              <option key={p} value={p} style={{ background: '#111' }}>
                {p === 'all' ? 'All Players' : p}
              </option>
            ))}
          </select>
          {/* Overlay toggle */}
          <button
            onClick={() => setShowOverlay(!showOverlay)}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.15)',
              background: showOverlay ? 'rgba(99,102,241,0.2)' : 'transparent',
              color: showOverlay ? '#a5b4fc' : '#888',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {showOverlay ? 'Overlay ON' : 'Overlay OFF'}
          </button>
        </div>
      </div>

      {/* Chat Sub-Tab */}
      {subTab === 'chat' && <FreeThrowChatPanel />}

      {/* Analysis Sub-Tab Content */}
      {subTab !== 'chat' && <>

      {/* Hero Cards */}
      <HeroCards attempts={filteredAttempts} playerSummary={activePlayerSummary} />

      {/* Speed Profile */}
      {selectedAttempt && (
        <FreeThrowSpeedProfile
          attempt={selectedAttempt}
          baseline={madeBaseline}
        />
      )}

      {/* Video + Metrics Panel */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16 }}>
        {/* Video Player */}
        <div style={{ ...cardStyle, padding: 0, position: 'relative', overflow: 'hidden', minHeight: 360 }}>
          {selectedAttempt?.video_url ? (
            <>
              <ReactPlayer
                ref={playerRef}
                url={selectedAttempt.video_url}
                width="100%"
                height="100%"
                controls
                playing={false}
                onProgress={({ playedSeconds }) => setCurrentTime(playedSeconds)}
                style={{ borderRadius: 12 }}
              />
              <FreeThrowVideoOverlay
                attempt={selectedAttempt}
                currentTime={currentTime}
                clipStart={selectedAttempt.clip_start || 0}
                visible={showOverlay}
              />
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 360, color: '#666' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>🎯</div>
                <div style={{ fontSize: 14 }}>Select an attempt to view video</div>
                <div style={{ fontSize: 11, color: '#555', marginTop: 4 }}>
                  Video overlay shows knee angle, heel elevation, ball tracking, and phase markers
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Metrics Panel */}
        <FreeThrowMetricsPanel
          attempt={selectedAttempt}
          playerSummary={activePlayerSummary}
        />
      </div>

      {/* Triptych Multi-Angle Analysis */}
      <TriptychSection triptych={activeTriptych} />

      {/* Shot-by-Shot Table */}
      <div>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: '0 0 8px' }}>
          Shot-by-Shot Analysis
          <span style={{ fontSize: 11, fontWeight: 400, color: '#888', marginLeft: 8 }}>
            sorted by speed pre-shot → outcome
          </span>
          <Tooltip text={TOOLTIPS.sortOrder} />
        </h3>
        <ShotTable
          attempts={filteredAttempts}
          selectedId={selectedAttemptId}
          onSelect={setSelectedAttemptId}
        />
      </div>

      {/* Biomechanical Tells (Phase 2) */}
      <BiomechanicalTells synthesis={activeSynthesis} />

      {/* Trend Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <OutcomeBreakdown attempts={filteredAttempts} />
        <SpeedByOutcome attempts={filteredAttempts} />
      </div>

      </>}

      {error && (
        <div style={{ ...cardStyle, borderLeft: '3px solid #ef4444', color: '#f87171' }}>
          Error: {error}
        </div>
      )}
    </div>
  );
}
